import React, { useState } from 'react';
import './AnnotationToolbar.css';

interface AnnotationToolbarProps {
  mode: 'view' | 'edit';
  onModeChange: (mode: 'view' | 'edit') => void;
  fileType: 'text' | 'image';
  selectedTool?: 'mark' | 'bbox' | 'polygon';
  onToolSelect?: (tool: 'mark' | 'bbox' | 'polygon') => void;
  selectedColor: string;
  onColorChange: (color: string) => void;
  onClearAll: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  markCount?: number;
  annotationCount?: number;
}

const AnnotationToolbar: React.FC<AnnotationToolbarProps> = ({
  mode,
  onModeChange,
  fileType,
  selectedTool,
  onToolSelect,
  selectedColor,
  onColorChange,
  onClearAll,
  onSave,
  onCancel,
  markCount = 0,
  annotationCount = 0,
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);

  const colorOptions = [
    '#FF6B6B', // Red
    '#4ECDC4', // Teal
    '#FFE66D', // Yellow
    '#95E1D3', // Mint
    '#F38181', // Pink
    '#AA96DA', // Purple
    '#FCBAD3', // Light Pink
    '#A8D8EA', // Light Blue
  ];

  const handleColorSelect = (color: string) => {
    onColorChange(color);
    setShowColorPicker(false);
  };

  const itemCount = fileType === 'text' ? markCount : annotationCount;

  return (
    <div className="annotation-toolbar">
      {/* Mode Toggle */}
      <div className="toolbar-section">
        <div className="mode-toggle">
          <button
            className={`mode-btn ${mode === 'view' ? 'active' : ''}`}
            onClick={() => onModeChange('view')}
          >
            👁️ View
          </button>
          <button
            className={`mode-btn ${mode === 'edit' ? 'active' : ''}`}
            onClick={() => onModeChange('edit')}
          >
            ✏️ Edit
          </button>
        </div>
      </div>

      {/* Tool Selection (Edit Mode Only) */}
      {mode === 'edit' && onToolSelect && (
        <div className="toolbar-section">
          <div className="tool-selection">
            {fileType === 'text' && (
              <button
                className={`tool-btn ${selectedTool === 'mark' ? 'active' : ''}`}
                onClick={() => onToolSelect('mark')}
                title="Highlight text"
              >
                🖍️ Mark Text
              </button>
            )}
            {fileType === 'image' && (
              <>
                <button
                  className={`tool-btn ${
                    selectedTool === 'bbox' ? 'active' : ''
                  }`}
                  onClick={() => onToolSelect('bbox')}
                  title="Draw bounding box"
                >
                  📦 Box
                </button>
                <button
                  className={`tool-btn ${
                    selectedTool === 'polygon' ? 'active' : ''
                  }`}
                  onClick={() => onToolSelect('polygon')}
                  title="Draw polygon"
                >
                  🔷 Polygon
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Color Picker */}
      {mode === 'edit' && (
        <div className="toolbar-section">
          <div className="color-picker-wrapper">
            <button
              className="color-btn"
              style={{ backgroundColor: selectedColor }}
              onClick={() => setShowColorPicker(!showColorPicker)}
              title="Select color"
            />
            {showColorPicker && (
              <div className="color-palette">
                {colorOptions.map((color) => (
                  <button
                    key={color}
                    className={`color-option ${
                      selectedColor === color ? 'selected' : ''
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorSelect(color)}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Item Count */}
      <div className="toolbar-section item-count">
        <span>
          {fileType === 'text' ? '📝' : '🖼️'} {itemCount}{' '}
          {fileType === 'text' ? 'marks' : 'annotations'}
        </span>
      </div>

      {/* Action Buttons */}
      <div className="toolbar-section actions">
        {mode === 'edit' && (
          <>
            {itemCount > 0 && (
              <button className="action-btn danger" onClick={onClearAll}>
                🗑️ Clear All
              </button>
            )}
            {onSave && (
              <button className="action-btn primary" onClick={onSave}>
                ✓ Save
              </button>
            )}
            {onCancel && (
              <button className="action-btn secondary" onClick={onCancel}>
                ✕ Cancel
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AnnotationToolbar;
